import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BellRing, CalendarPlus, Loader2, Mail, Pencil, Send, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "bg-[#0B0F19] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-sm text-starlight outline-none focus:border-[#D4AF37]";
const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");

function OfferForm({ readings, initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { slug: readings[0]?.slug, start: "", first_end: "", end: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form data-testid="offer-form" onSubmit={(e) => { e.preventDefault(); onSave(f); }}
      className="bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-4 grid gap-3 sm:grid-cols-5 items-end">
      <label className="text-xs text-[#A7B0C0] sm:col-span-2">Free reading
        <select data-testid="offer-slug" value={f.slug} onChange={set("slug")} className={`${field} w-full mt-1`}>
          {readings.map((r) => <option key={r.slug} value={r.slug}>{r.title}</option>)}
        </select>
      </label>
      <label className="text-xs text-[#A7B0C0]">Starts<input data-testid="offer-start" type="date" required value={f.start} onChange={set("start")} className={`${field} w-full mt-1`} /></label>
      <label className="text-xs text-[#A7B0C0]">“Free until”<input data-testid="offer-first-end" type="date" required value={f.first_end} onChange={set("first_end")} className={`${field} w-full mt-1`} /></label>
      <label className="text-xs text-[#A7B0C0]">Extended to<input data-testid="offer-end" type="date" required value={f.end} onChange={set("end")} className={`${field} w-full mt-1`} /></label>
      <div className="sm:col-span-5 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} data-testid="offer-cancel" className="text-sm text-[#A7B0C0] px-4 py-2">Cancel</button>
        <button data-testid="offer-save" className="bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] text-sm px-5 py-2 rounded-full transition-colors">Save offer</button>
      </div>
      <p className="sm:col-span-5 text-[11px] text-[#A7B0C0]">Dates start at midnight (UK time is close enough: midnight UTC). The countdown shows “Free until” first, then switches to “extended” until the final date.</p>
    </form>
  );
}

function ClaimBoard({ rows, slug }) {
  return (
    <div className="mt-3 bg-[#0B0F19] rounded-xl p-3" data-testid={`offer-leaderboard-${slug}`}>
      <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] gold-text mb-2"><Trophy size={13} /> Claim leaderboard · friends brought in via share links</p>
      {rows.length === 0 ? <p className="text-xs text-[#A7B0C0]" data-testid={`offer-leaderboard-empty-${slug}`}>No friends have joined through a share link during this offer yet.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] text-[#A7B0C0] text-left"><th className="font-normal py-1">#</th><th className="font-normal">Member</th><th className="font-normal text-right">Friends joined</th><th className="font-normal text-right">Friends claimed</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.email + i} className="border-t border-[#D4AF37]/10" data-testid={`offer-leaderboard-row-${slug}-${i}`}>
                <td className="py-1.5 gold-text">{i + 1}</td>
                <td className="text-starlight">{r.name}<span className="block text-[11px] text-[#A7B0C0]">{r.email}</span></td>
                <td className="text-right">{r.joined}</td>
                <td className="text-right gold-text">{r.claimed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OfferRow({ p, current, onEdit, onDelete, onTest, onSend, onRemind }) {
  const live = current?.id === p.id;
  const past = new Date(p.end) < new Date();
  const s = p.stats;
  return (
    <div data-testid={`offer-row-${p.slug}-${p.id.slice(0, 6)}`} className={`bg-[#0F1523] border rounded-2xl p-4 ${live ? "border-[#D4AF37]/70" : "border-[#D4AF37]/15"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-lg text-starlight">{p.title}</p>
        <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${live ? "bg-[#D4AF37] text-[#04060B]" : past ? "bg-[#232838] text-[#A7B0C0]" : "border border-[#D4AF37]/40 gold-text"}`}>
          {live ? (current.extended ? "Live · extended" : "Live") : past ? "Ended" : "Scheduled"}
        </span>
        <div className="ml-auto flex gap-1">
          {!past && <button title="Edit dates" data-testid={`offer-edit-${p.id.slice(0, 6)}`} onClick={onEdit} className="p-2 text-[#A7B0C0] hover:text-starlight"><Pencil size={15} /></button>}
          <button title="Delete" data-testid={`offer-delete-${p.id.slice(0, 6)}`} onClick={onDelete} className="p-2 text-[#A7B0C0] hover:text-red-300"><Trash2 size={15} /></button>
        </div>
      </div>
      <p className="text-sm text-[#A7B0C0] mt-1">{day(p.start)} → free until {day(p.first_end)} → extended to {day(p.end)}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3" data-testid={`offer-stats-${p.slug}`}>
        {[["Claimed", s.claims], ["New subscribers", s.new_subscribers], ["Still subscribed", s.still_subscribed], ["Offer emails sent", s.emails_sent], ["Last-day reminders", s.reminders_sent]].map(([l, v]) => (
          <div key={l} className="bg-[#0B0F19] rounded-xl px-3 py-2"><p className="text-xl gold-text">{v}</p><p className="text-[11px] text-[#A7B0C0]">{l}</p></div>
        ))}
      </div>
      <ClaimBoard rows={p.leaderboard || []} slug={p.slug} />
      {!past && <p className="text-[11px] text-[#A7B0C0] mt-2">A last-day reminder goes automatically to every member who hasn't claimed, 24 hours before the final end date.</p>}
      {!past && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid={`offer-email-test-${p.slug}`} onClick={onTest} className="flex items-center gap-1.5 text-xs border border-[#D4AF37]/40 gold-text px-3 py-1.5 rounded-full hover:bg-[#D4AF37]/10"><Mail size={13} /> Send me a test email</button>
          <button data-testid={`offer-email-send-${p.slug}`} onClick={onSend} className="flex items-center gap-1.5 text-xs bg-[#D4AF37] text-[#04060B] px-3 py-1.5 rounded-full hover:bg-[#e6c04e]"><Send size={13} /> Email free members</button>
          <button data-testid={`offer-reminder-test-${p.slug}`} onClick={onRemind} className="flex items-center gap-1.5 text-xs border border-[#D4AF37]/40 gold-text px-3 py-1.5 rounded-full hover:bg-[#D4AF37]/10"><BellRing size={13} /> Test the last-day reminder</button>
        </div>
      )}
    </div>
  );
}

export default function Offers({ token }) {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(null);
  const headers = { "X-Analytics-Token": token };
  const load = useCallback(() => axios.get(`${API}/analytics/promotions`, { headers }).then((r) => setD(r.data)).catch(() => setD({ promotions: [], readings: [] })), [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const save = async (f) => {
    const body = { slug: f.slug, start: `${f.start}T00:00:00Z`, first_end: `${f.first_end}T00:00:00Z`, end: `${f.end}T00:00:00Z` };
    try {
      if (editing === "new") await axios.post(`${API}/analytics/promotions`, body, { headers });
      else await axios.put(`${API}/analytics/promotions/${editing.id}`, body, { headers });
      toast.success("Offer saved"); setEditing(null); load();
    } catch (e) { err(e); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete the ${p.title} offer?`)) return;
    try { await axios.delete(`${API}/analytics/promotions/${p.id}`, { headers }); toast.success("Offer deleted"); load(); } catch (e) { err(e); }
  };
  const test = async (p) => {
    try { const { data } = await axios.post(`${API}/analytics/promotions/${p.id}/email-test`, {}, { headers }); data.sent ? toast.success(`Test sent to ${data.to}`) : toast.error("Test couldn't be sent. Please try again in a minute."); } catch (e) { err(e); }
  };
  const remind = async (p) => {
    try { const { data } = await axios.post(`${API}/analytics/promotions/${p.id}/reminder-test`, {}, { headers }); data.sent ? toast.success(`Test reminder sent to ${data.to}`) : toast.error("Test couldn't be sent. Please try again in a minute."); } catch (e) { err(e); }
  };
  const send = async (p) => {
    if (!window.confirm(`Email the ${p.title} offer to every free member with email reminders on? Each member only ever gets it once.`)) return;
    try { await axios.post(`${API}/analytics/promotions/${p.id}/email-send`, {}, { headers }); toast.success("Sending now. The count updates as emails go out."); setTimeout(load, 8000); } catch (e) { err(e); }
  };
  return (
    <section className="mt-10 mb-10" data-testid="offers-section">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-xl text-starlight">Free reading offers</h2>
        <button data-testid="offer-add" onClick={() => setEditing("new")} className="ml-auto flex items-center gap-1.5 text-sm gold-text border border-[#D4AF37]/40 px-3 py-1.5 rounded-full hover:bg-[#D4AF37]/10"><CalendarPlus size={14} /> Plan an offer</button>
      </div>
      {!d ? <Loader2 className="animate-spin gold-text" size={18} /> : (
        <div className="space-y-3">
          {editing && <OfferForm readings={d.readings} onCancel={() => setEditing(null)} onSave={save}
            initial={editing === "new" ? null : { slug: editing.slug, start: toLocal(editing.start), first_end: toLocal(editing.first_end), end: toLocal(editing.end) }} />}
          {d.promotions.length === 0 && <p className="text-sm text-[#A7B0C0]">No offers planned yet.</p>}
          {d.promotions.map((p) => (
            <OfferRow key={p.id} p={p} current={d.current} onEdit={() => setEditing(p)} onDelete={() => del(p)} onTest={() => test(p)} onSend={() => send(p)} onRemind={() => remind(p)} />
          ))}
        </div>
      )}
    </section>
  );
}
